// API
import { useApiSWRReference } from '@/hooks/useApiSWR'

import {
    Input,
    InputBase,
    Combobox,
    useCombobox,
    CheckIcon,
    Button,
} from '@mantine/core'

import { useDidUpdate } from '@mantine/hooks'

// React
import { useState, useEffect } from 'react'

export default function ShopifyProductMultiSelect({
    label = 'Selectionner des variantes Shopify',
    value = [],
    onChange = () => null,
}) {
    const { data: products } = useApiSWRReference('/shopify/products/list')

    // Combobox
    const combobox = useCombobox()
    const [selectedVariants, setSelectedVariants] = useState(value ?? [])

    const onOptionSubmit = (option) => {
        // If selected option is a product
        if (option.option_type === 'product') {
            // If no variants are selected, add all variants
            const hasAnyVariantInThisProduct =
                option.variants.some((variant) => {
                    return selectedVariants.includes(String(variant.id))
                })

            if (!hasAnyVariantInThisProduct) {
                // Add all variants to value
                setSelectedVariants([
                    ...selectedVariants,
                    ...option.variants.map((variant) =>
                        String(variant.id)
                    ),
                ])
            } else {
                // Remove all variants from value
                setSelectedVariants(
                    selectedVariants.filter(
                        (item) =>
                            !option.variants
                                .map((variant) => String(variant.id))
                                .includes(item)
                    )
                )
            }
        }

        // If selected option is a variant
        if (option.option_type === 'variant') {
            // If in value array, remove it
            if (selectedVariants.find((item) => item == option.id)) {
                setSelectedVariants(
                    selectedVariants.filter((item) => item != option.id)
                )
            } else {
                setSelectedVariants([...selectedVariants, String(option.id)])
            }
        }
    }

    // Search
    const [search, setSearch] = useState('')

    const options =
        products?.filter((product) => {
            const searchLower = search.toLowerCase()
            const titleLower = product.title.toLowerCase()

            return titleLower.includes(searchLower)
        }) ?? []

    useDidUpdate(() => {
        onChange(selectedVariants)
    }, [selectedVariants])

    return (
        <>
            <Combobox
                resetSelectionOnOptionHover
                store={combobox}
                onOptionSubmit={onOptionSubmit}
                withinPortal={false}
            >
                <Combobox.Target>
                    <InputBase
                        label={label}
                        component="button"
                        type="button"
                        pointer
                        rightSection={<Combobox.Chevron />}
                        rightSectionPointerEvents="none"
                        onClick={() => combobox.toggleDropdown()}
                    >
                        {selectedVariants.length === 0 && (
                            <Input.Placeholder>
                                Selectionner des variantes Shopify
                            </Input.Placeholder>
                        )}

                        {selectedVariants.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                {selectedVariants.length} variante(s)
                                sélectionnée(s)
                            </div>
                        )}
                    </InputBase>
                </Combobox.Target>

                <Combobox.Dropdown>
                    <Combobox.Search
                        placeholder="Chercher un produit Shopify"
                        value={search}
                        onChange={(event) =>
                            setSearch(event.currentTarget.value)
                        }
                    />

                    <Combobox.Options mah={600} style={{ overflowY: 'auto' }}>
                        {options.map((option) => {
                            const selectedVariantsInProduct =
                                selectedVariants.filter((item) =>
                                    option.variants
                                        .map((variant) => String(variant.id))
                                        .includes(item)
                                )

                            return (
                                <>
                                    <Combobox.Option
                                        key={option.id}
                                        value={{
                                            ...option,
                                            option_type: 'product',
                                        }}
                                        className="flex items-center justify-between"
                                    >
                                        <span className="font-medium">
                                            {option.title}
                                        </span>

                                        <span className="text-gray-400 text-xs">
                                            {selectedVariantsInProduct.length >
                                                0 &&
                                                selectedVariantsInProduct.length +
                                                    ' / ' +
                                                    option.variants
                                                        .length}
                                        </span>
                                    </Combobox.Option>

                                    {selectedVariantsInProduct.length > 0 &&
                                        option.variants.map(
                                            (variant) => (
                                                <Combobox.Option
                                                    active={true}
                                                    key={variant.id}
                                                    value={{
                                                        ...variant,
                                                        option_type: 'variant',
                                                    }}
                                                    className="flex w-full justify-between"
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <div>
                                                            {selectedVariants.find(
                                                                (item) =>
                                                                    item ==
                                                                    variant.id
                                                            ) && (
                                                                <CheckIcon
                                                                    size={10}
                                                                />
                                                            )}
                                                        </div>

                                                        <span className="flex items-center gap-2">
                                                            {variant.title}
                                                        </span>
                                                    </div>

                                                    <span>{variant.sku}</span>
                                                </Combobox.Option>
                                            )
                                        )}
                                </>
                            )
                        })}
                    </Combobox.Options>
                </Combobox.Dropdown>
            </Combobox>
        </>
    )
}
